import type { BillingAccount, BillingEstimate, BillingHistoryEntry } from '~/gql/billing/billing'
import { useQuery } from '@apollo/client/react'
import { BillingMode } from '@zeus/index'
import { useState } from 'react'
import { GQL_BILLING_ACCOUNT, GQL_BILLING_ESTIMATE, GQL_BILLING_HISTORY } from '~/gql/billing/billing'
import { client } from '~/helpers/apollo'
import { tourAnchor } from '~/helpers/tour/anchors'
import BillingHistoryTable from '~/pages/backoffice/admin/billing/BillingHistoryTable'
import BillingMonthHeader from '~/pages/backoffice/admin/billing/BillingMonthHeader'
import BillingPortalNotice from '~/pages/backoffice/admin/billing/BillingPortalNotice'
import BillingProfileForm from '~/pages/backoffice/admin/billing/BillingProfileForm'
import CheckoutReturnNotice from '~/pages/backoffice/admin/billing/CheckoutReturnNotice'
import EstimateCards from '~/pages/backoffice/admin/billing/EstimateCards'
import ManagedPlanNotice from '~/pages/backoffice/admin/billing/ManagedPlanNotice'
import { currentMonth } from '~/pages/backoffice/admin/billing/month'
import SubscriptionSummary from '~/pages/backoffice/admin/billing/SubscriptionSummary'
import TierOffers from '~/pages/backoffice/admin/billing/TierOffers'
import { useCheckoutReturn } from '~/pages/backoffice/admin/billing/use-checkout-return'
import { usePortalReturn } from '~/pages/backoffice/admin/billing/use-portal-return'

/**
 * Onglet de facturation de l'organisation (#51), réservé à l'admin : il est le
 * titulaire du contrat, il gère lui-même sa formule et ses coordonnées.
 *
 * Contrepartie du prix à l'usage : le client voit en continu ce sur quoi il
 * sera facturé. Le palier se souscrit sur Stripe Checkout (#352) et ne change
 * qu'à la confirmation du paiement, par le webhook.
 */
const HISTORY_MONTHS = 12

function AdminBilling() {
  const [month, setMonth] = useState(() => currentMonth())

  const { data: accountData, refetch: refetchAccount } = useQuery(GQL_BILLING_ACCOUNT, {
    client,
    fetchPolicy: 'network-only',
  })
  const { data: estimateData, refetch: refetchEstimate } = useQuery(GQL_BILLING_ESTIMATE, {
    client,
    variables: { month },
    fetchPolicy: 'network-only',
  })
  const { data: historyData, refetch: refetchHistory } = useQuery(GQL_BILLING_HISTORY, {
    client,
    variables: { months: HISTORY_MONTHS },
    fetchPolicy: 'network-only',
  })

  const account = accountData?.billingAccount as unknown as BillingAccount | undefined
  const estimate = estimateData?.billingEstimate as unknown as BillingEstimate | undefined
  const history = (historyData?.billingHistory ?? []) as unknown as BillingHistoryEntry[]

  // Le palier activé par le webhook change l'estimation et l'historique (recalculé avec lui).
  // Retour du portail client (#353) : le palier, l'estimation et l'historique peuvent avoir changé.
  usePortalReturn(() => Promise.all([refetchAccount(), refetchEstimate(), refetchHistory()]))
  const checkoutStatus = useCheckoutReturn({
    subscribed: account?.subscribed ?? false,
    poll: refetchAccount,
    onConfirmed: () => void Promise.all([refetchEstimate(), refetchHistory()]),
  })

  return (
    <div className="space-y-4">
      <BillingMonthHeader month={month} onChange={setMonth} />
      {checkoutStatus && <CheckoutReturnNotice status={checkoutStatus} />}

      <div {...tourAnchor('billing.estimate')}>
        <EstimateCards estimate={estimate} />
      </div>
      <PlanSection account={account} awaitingPayment={checkoutStatus === 'pending' || checkoutStatus === 'slow'} />
      <div {...tourAnchor('billing.profile')}>
        {account?.billingPortalAvailable ? <BillingPortalNotice /> : <BillingProfileForm account={account} />}
      </div>
      <div {...tourAnchor('billing.history')}>
        <BillingHistoryTable rows={history} />
      </div>
    </div>
  )
}

/**
 * Ce que l'admin peut faire de son palier : rien s'il est géré, le voir s'il paie,
 * sinon souscrire. Pas d'offres pendant qu'un paiement se confirme : un second
 * clic ouvrirait un second abonnement avant que le webhook du premier soit passé.
 */
function PlanSection({ account, awaitingPayment }: { account?: BillingAccount, awaitingPayment: boolean }) {
  if (!account || awaitingPayment) return null
  if (account.mode === BillingMode.MANAGED) return <ManagedPlanNotice />
  if (account.subscribed) return <SubscriptionSummary account={account} />
  return <TierOffers />
}

export default AdminBilling
